import React, { forwardRef, type ComponentProps, type ElementRef, type ComponentPropsWithoutRef } from 'react';
import { cva } from 'class-variance-authority';
import { Popover as PopoverPrimitive } from 'radix-ui';

import { cn } from '@tectonics/utils/utils';

function Popover({ ...props }: ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ className, ...props }: ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" type="button" className={cn('flex bg-transparent border-0 items-center justify-center', className)} {...props} />;
}

type PopoverLookAndFeel = 'default' | 'volcanes' | 'shopify';

type PopoverContentProps = ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  lookAndFeel?: PopoverLookAndFeel;
};

const popoverContentVariants = cva(
  'z-50 inline-flex w-fit max-w-[384px] origin-(--radix-popover-content-transform-origin) items-center gap-1.5 text-sm outline-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
  {
    variants: {
      lookAndFeel: {
        default:
          'bg-popover-bg text-popover-fg border border-popover-border shadow-md p-0 rounded-md',
        volcanes:
          'bg-popover-bg text-popover-fg border border-popover-border shadow-md p-0 rounded-[4px]',
        shopify:
          'bg-popover-bg text-popover-fg border border-popover-border shadow-md p-0 rounded-[3px]',
      },
    },
    defaultVariants: {
      lookAndFeel: 'default',
    },
  },
);

const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = 'center', sideOffset = 0, children, lookAndFeel, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      className={cn(
        popoverContentVariants({ lookAndFeel }),
        className,
      )}
      {...props}
      data-look-and-feel={lookAndFeel}
    >
      {children}
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

const PopoverArrow = forwardRef<
  ElementRef<typeof PopoverPrimitive.Arrow>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <PopoverPrimitive.Arrow
    ref={ref}
    data-slot="popover-arrow"
    className={cn(className)}
    {...props}
  />
));
PopoverArrow.displayName = PopoverPrimitive.Arrow.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverArrow };
